import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoiceAPI } from '../api/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowsClockwise, 
  Plus, 
  Trash, 
  PencilSimple, 
  Play,
  Pause,
  CurrencyDollar,
  CalendarBlank,
  User,
  Lightning,
  SpinnerGap,
  CheckCircle
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

const frequencyLabels = {
  weekly: 'Weekly',
  biweekly: 'Every 2 Weeks',
  monthly: 'Monthly',
  quarterly: 'Quarterly'
};

const RecurringInvoices = () => {
  const navigate = useNavigate();
  const [recurringInvoices, setRecurringInvoices] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRecurring, setEditingRecurring] = useState(null);
  const [generating, setGenerating] = useState(null);
  const [formData, setFormData] = useState({
    contractor_id: '',
    job_title: '',
    line_items: [{ description: '', quantity: 1, unit_price: 0, amount: 0 }],
    tax_rate: 0,
    notes: '',
    frequency: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    due_days: 30
  });

  useEffect(() => {
    fetchRecurringInvoices();
    fetchContractors();
  }, []);

  const fetchRecurringInvoices = async () => {
    try {
      const response = await invoiceAPI.getRecurringInvoices();
      setRecurringInvoices(response.data.recurring_invoices || []);
    } catch (error) {
      console.error('Error fetching recurring invoices:', error);
      toast({
        title: 'Error',
        description: 'Failed to load recurring invoices',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchContractors = async () => {
    try {
      // Get conversations to find contractors
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/conversations`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      // Filter to get unique contractors
      const uniqueContractors = data.conversations?.filter(c => c.partner_type === 'contractor') || [];
      setContractors(uniqueContractors);
    } catch (error) {
      console.error('Error fetching contractors:', error);
    }
  };

  const handleLineItemChange = (index, field, value) => {
    const newLineItems = [...formData.line_items];
    newLineItems[index][field] = field === 'description' ? value : parseFloat(value) || 0;
    if (field === 'quantity' || field === 'unit_price') {
      newLineItems[index].amount = newLineItems[index].quantity * newLineItems[index].unit_price;
    }
    setFormData({ ...formData, line_items: newLineItems });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { description: '', quantity: 1, unit_price: 0, amount: 0 }]
    });
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length > 1) {
      const newLineItems = formData.line_items.filter((_, i) => i !== index);
      setFormData({ ...formData, line_items: newLineItems });
    }
  };

  const calculateSubtotal = () => {
    return formData.line_items.reduce((sum, item) => sum + item.amount, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
      };

      if (editingRecurring) {
        await invoiceAPI.updateRecurringInvoice(editingRecurring._id, submitData);
        toast({ title: 'Success', description: 'Recurring invoice updated successfully' });
      } else {
        await invoiceAPI.createRecurringInvoice(submitData);
        toast({ title: 'Success', description: 'Recurring invoice created successfully' });
      }
      setShowCreateModal(false);
      setEditingRecurring(null);
      resetForm();
      fetchRecurringInvoices();
    } catch (error) {
      console.error('Error saving recurring invoice:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save recurring invoice',
        variant: 'destructive',
      });
    }
  };

  const handleToggleActive = async (recurring) => {
    try {
      await invoiceAPI.updateRecurringInvoice(recurring._id, { is_active: !recurring.is_active });
      toast({ 
        title: recurring.is_active ? 'Paused' : 'Activated',
        description: `Recurring invoice ${recurring.is_active ? 'paused' : 'activated'} successfully`
      });
      fetchRecurringInvoices();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  const handleGenerateNow = async (recurringId) => {
    setGenerating(recurringId);
    try {
      const response = await invoiceAPI.generateRecurringNow(recurringId);
      toast({ 
        title: 'Invoice Generated',
        description: `Invoice ${response.data.invoice?.invoice_number || ''} created successfully`
      });
      fetchRecurringInvoices();
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to generate invoice',
        variant: 'destructive',
      });
    } finally {
      setGenerating(null);
    }
  };

  const handleDelete = async (recurringId) => {
    if (!window.confirm('Are you sure you want to delete this recurring invoice?')) return;
    try {
      await invoiceAPI.deleteRecurringInvoice(recurringId);
      toast({ title: 'Success', description: 'Recurring invoice deleted successfully' });
      fetchRecurringInvoices();
    } catch (error) {
      console.error('Error deleting recurring invoice:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete recurring invoice',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      contractor_id: '',
      job_title: '',
      line_items: [{ description: '', quantity: 1, unit_price: 0, amount: 0 }],
      tax_rate: 0,
      notes: '',
      frequency: 'monthly',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      due_days: 30
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-neutral-900 flex items-center justify-center">
        <SpinnerGap className="h-12 w-12 text-[#2B4C6F] animate-spin" weight="bold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-neutral-900 pb-24 transition-colors duration-300" data-testid="recurring-invoices-page">
      {/* Header */}
      <div className="bg-[#2B4C6F] text-white py-6 border-b-4 border-black dark:border-neutral-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-black flex items-center gap-3">
                <ArrowsClockwise size={36} weight="bold" />
                Recurring Invoices
              </h1>
              <p className="text-blue-100 mt-1">Automate your billing with scheduled invoices</p>
            </div>
            <Button
              onClick={() => {
                resetForm();
                setEditingRecurring(null);
                setShowCreateModal(true);
              }}
              className="h-14 px-6 bg-white text-[#2B4C6F] font-bold text-lg rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:translate-y-1 hover:shadow-[2px_2px_0px_#000] transition-all"
              data-testid="create-recurring-btn"
            >
              <Plus size={24} weight="bold" className="mr-2" />
              New Schedule
            </Button>
          </div>
        </div>
      </div>

      {/* Recurring Invoices List */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {recurringInvoices.length === 0 ? (
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-600 rounded-xl p-12 text-center shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <ArrowsClockwise size={64} weight="bold" className="mx-auto text-gray-400 dark:text-neutral-500 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No Recurring Invoices</h3>
            <p className="text-gray-600 dark:text-neutral-400 mb-6">Set up automatic invoicing on a schedule</p>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="h-12 px-6 bg-[#2B4C6F] text-white font-bold rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:translate-y-1 hover:shadow-[1px_1px_0px_#000] transition-all"
            >
              <Plus size={20} weight="bold" className="mr-2" />
              Create Schedule
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {recurringInvoices.map((recurring) => (
              <div
                key={recurring._id}
                className={`bg-white dark:bg-neutral-800 border-2 ${recurring.is_active ? 'border-black dark:border-neutral-600' : 'border-gray-300 dark:border-neutral-700'} rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] ${!recurring.is_active && 'opacity-60'}`}
                data-testid={`recurring-card-${recurring._id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {recurring.job_title || 'Recurring Invoice'}
                      </h3>
                      <Badge className={`${recurring.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-neutral-700 dark:text-neutral-400'} border-2 ${recurring.is_active ? 'border-green-300' : 'border-gray-300 dark:border-neutral-600'}`}>
                        {recurring.is_active ? 'Active' : 'Paused'}
                      </Badge>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-gray-600 dark:text-neutral-400">
                      <div className="flex items-center gap-2">
                        <User size={18} weight="bold" />
                        <span>{recurring.contractor_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <ArrowsClockwise size={18} weight="bold" />
                        <span className="font-medium">{frequencyLabels[recurring.frequency]}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CurrencyDollar size={18} weight="bold" className="text-green-600 dark:text-green-400" />
                        <span className="font-bold text-green-600 dark:text-green-400">${recurring.total?.toFixed(2)}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500 dark:text-neutral-500">
                      <span>Next: {formatDate(recurring.next_generation_date)}</span>
                      <span>•</span>
                      <span>{recurring.invoices_generated} invoices generated</span>
                      {recurring.end_date && (
                        <>
                          <span>•</span>
                          <span>Ends: {formatDate(recurring.end_date)}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => handleGenerateNow(recurring._id)}
                      disabled={generating === recurring._id}
                      variant="outline"
                      className="h-12 px-4 font-bold border-2 border-black dark:border-neutral-600 rounded-xl hover:bg-gray-100 dark:hover:bg-neutral-700"
                      data-testid={`generate-now-${recurring._id}`}
                    >
                      {generating === recurring._id ? (
                        <SpinnerGap size={20} weight="bold" className="animate-spin" />
                      ) : (
                        <>
                          <Lightning size={20} weight="bold" className="mr-2" />
                          Generate Now
                        </>
                      )}
                    </Button>
                    <button
                      onClick={() => handleToggleActive(recurring)}
                      className={`h-12 w-12 flex items-center justify-center rounded-xl border-2 ${recurring.is_active ? 'border-yellow-500 bg-yellow-100 dark:bg-yellow-900/30 hover:bg-yellow-200 dark:hover:bg-yellow-900/50' : 'border-green-500 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50'}`}
                      data-testid={`toggle-active-${recurring._id}`}
                    >
                      {recurring.is_active ? (
                        <Pause size={20} weight="bold" className="text-yellow-600 dark:text-yellow-400" />
                      ) : (
                        <Play size={20} weight="bold" className="text-green-600 dark:text-green-400" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDelete(recurring._id)}
                      className="h-12 w-12 flex items-center justify-center bg-red-100 dark:bg-red-900/30 rounded-xl border-2 border-red-300 dark:border-red-800 hover:bg-red-200 dark:hover:bg-red-900/50"
                      data-testid={`delete-recurring-${recurring._id}`}
                    >
                      <Trash size={20} weight="bold" className="text-red-600 dark:text-red-400" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-600 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#2B4C6F]">
            <div className="p-6">
              <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6">
                {editingRecurring ? 'Edit Recurring Invoice' : 'Create Recurring Invoice'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Contractor Selection */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-neutral-300 mb-2">
                    Contractor *
                  </label>
                  <input
                    type="text"
                    value={formData.contractor_id}
                    onChange={(e) => setFormData({ ...formData, contractor_id: e.target.value })}
                    placeholder="Enter contractor ID"
                    required
                    className="w-full h-12 px-4 border-2 border-black dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-gray-900 dark:text-white font-medium"
                    data-testid="contractor-id-input"
                  />
                  <p className="text-sm text-gray-500 dark:text-neutral-500 mt-1">
                    Enter the contractor's user ID
                  </p>
                </div>

                {/* Job Title */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-neutral-300 mb-2">
                    Invoice Title
                  </label>
                  <input
                    type="text"
                    value={formData.job_title}
                    onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                    placeholder="e.g., Monthly Maintenance"
                    className="w-full h-12 px-4 border-2 border-black dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-gray-900 dark:text-white font-medium"
                  />
                </div>

                {/* Frequency & Schedule */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-neutral-300 mb-2">
                      Frequency *
                    </label>
                    <select
                      value={formData.frequency}
                      onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                      className="w-full h-12 px-4 border-2 border-black dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-gray-900 dark:text-white font-medium"
                      data-testid="frequency-select"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Every 2 Weeks</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-neutral-300 mb-2">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                      className="w-full h-12 px-4 border-2 border-black dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-gray-900 dark:text-white font-medium"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-neutral-300 mb-2">
                      End Date (Optional)
                    </label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="w-full h-12 px-4 border-2 border-black dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-gray-900 dark:text-white font-medium"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-neutral-300 mb-2">
                      Payment Due (Days)
                    </label>
                    <input
                      type="number"
                      value={formData.due_days}
                      onChange={(e) => setFormData({ ...formData, due_days: parseInt(e.target.value) || 30 })}
                      min="1"
                      className="w-full h-12 px-4 border-2 border-black dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-gray-900 dark:text-white font-medium"
                    />
                  </div>
                </div>

                {/* Line Items */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-neutral-300 mb-2">
                    Line Items
                  </label>
                  <div className="space-y-3">
                    {formData.line_items.map((item, index) => (
                      <div key={index} className="flex gap-2 items-start">
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                          placeholder="Description"
                          required
                          className="flex-1 h-12 px-4 border-2 border-black dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-gray-900 dark:text-white font-medium"
                        />
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                          placeholder="Qty"
                          min="0"
                          step="0.5"
                          required
                          className="w-20 h-12 px-3 border-2 border-black dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-gray-900 dark:text-white font-medium text-center"
                        />
                        <input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => handleLineItemChange(index, 'unit_price', e.target.value)}
                          placeholder="Price"
                          min="0"
                          step="0.01"
                          required
                          className="w-28 h-12 px-3 border-2 border-black dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-gray-900 dark:text-white font-medium"
                        />
                        <div className="w-24 h-12 px-3 border-2 border-gray-300 dark:border-neutral-600 rounded-xl bg-gray-100 dark:bg-neutral-700 flex items-center justify-center text-gray-700 dark:text-neutral-300 font-bold">
                          ${item.amount.toFixed(2)}
                        </div>
                        {formData.line_items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLineItem(index)}
                            className="h-12 w-12 flex items-center justify-center bg-red-100 dark:bg-red-900/30 rounded-xl hover:bg-red-200 dark:hover:bg-red-900/50"
                          >
                            <Trash size={20} weight="bold" className="text-red-600 dark:text-red-400" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    onClick={addLineItem}
                    variant="outline"
                    className="mt-3 h-10 border-2 border-dashed border-gray-400 dark:border-neutral-500 text-gray-600 dark:text-neutral-400 hover:border-[#2B4C6F] hover:text-[#2B4C6F]"
                  >
                    <Plus size={18} weight="bold" className="mr-2" />
                    Add Line Item
                  </Button>
                </div>

                {/* Tax Rate */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 dark:text-neutral-300 mb-2">
                    Tax Rate (%)
                  </label>
                  <input
                    type="number"
                    value={formData.tax_rate}
                    onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="100"
                    step="0.1"
                    className="w-full h-12 px-4 border-2 border-black dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-700 text-gray-900 dark:text-white font-medium"
                  />
                </div>

                {/* Totals Preview */}
                <div className="bg-gray-100 dark:bg-neutral-700 rounded-xl p-4">
                  <div className="flex justify-between text-gray-700 dark:text-neutral-300 mb-2">
                    <span>Subtotal:</span>
                    <span className="font-bold">${calculateSubtotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-700 dark:text-neutral-300 mb-2">
                    <span>Tax ({formData.tax_rate}%):</span>
                    <span className="font-bold">${(calculateSubtotal() * formData.tax_rate / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xl font-black text-gray-900 dark:text-white pt-2 border-t border-gray-300 dark:border-neutral-600">
                    <span>Total per Invoice:</span>
                    <span className="text-green-600 dark:text-green-400">
                      ${(calculateSubtotal() * (1 + formData.tax_rate / 100)).toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingRecurring(null);
                      resetForm();
                    }}
                    className="flex-1 h-14 font-bold text-lg border-2 border-black dark:border-neutral-600 rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 h-14 bg-[#2B4C6F] text-white font-bold text-lg rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:translate-y-1 hover:shadow-[2px_2px_0px_#000] transition-all"
                    data-testid="save-recurring-btn"
                  >
                    {editingRecurring ? 'Update Schedule' : 'Create Schedule'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecurringInvoices;
